/* eslint-disable react/no-unstable-nested-components */
import { FC, useMemo, useState } from 'react';
import styles from './CurrentSettings.module.css';
import Table from '@/components/Tables/Table/Table';
import { CurrentSettingsProps } from './CurrentSettings.types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { X } from 'react-feather';
import { BundleUpchargeStore, BundleUpchargeStoreGroup, Patches } from '@/types/bundles.types';
import { PriceInfoGroupStore, PriceInfoStores } from './PrinceInfo';
import BundleUpchargesPriceDeviationDrawer from '@/pages/Franchisee/Bundles/BundleUpcharges/components/PriceDeviation/BundleUpchargePriceDeviationDrawer';

export const patchesIncludesPrice = (patches: Patches[], header: { description: string; isLock: boolean }) => {
  const product = patches.find((item: any) => item?.description === header.description);
  return product?.price ? `$${product.price}` : '';
};

const CurrentSettings: FC<CurrentSettingsProps> = ({
  tableDataStoreGroup,
  tableDataStore,
  columnsData,
  removeStoreGroup,
  removeStore,
}) => {
  const [bundleUpchargePriceDeviationData, setBundleUpchargePriceDeviationData] = useState({
    isBundleUpchargePriceDeviationOpen: false,
    cameFromEdit: false,
    type: '',
    groupId: '',
  });

  const { t } = useTranslation();
  const renderStoreGroup = useMemo(() => tableDataStoreGroup.length > 0, []);

  const columnHeaders = columnsData.map((patchType) => ({
    description: patchType.description,
    isLock: patchType.isLock,
  }));

  const keyColumnStoreGroup = {
    id: 'storeGroupName',
    accessorFn: (originalRow: BundleUpchargeStoreGroup) => originalRow.storeGroupName,
    accessorKey: 'storeGroupName',
    header: 'Store Group',
    enableSorting: true,
  };

  const keyColumnStore = {
    id: 'storeNo',
    accessorFn: (originalRow: BundleUpchargeStore) => originalRow.storeNo,
    accessorKey: 'storeNo',
    header: 'Store',
    enableSorting: true,
  };

  const handleBundleUpchargePriceDeviationDrawerOpen = (type: string, groupId: string) => {
    setBundleUpchargePriceDeviationData((prevState) => ({
      ...prevState,
      isBundleUpchargePriceDeviationOpen: true,
      groupId,
      type,
    }));
  };

  const handleInfoClick = (product: any, storeGroup: any) => {
    handleBundleUpchargePriceDeviationDrawerOpen(product, storeGroup);
  };

  const columnList = [];
  columnList.push(renderStoreGroup ? keyColumnStoreGroup : keyColumnStore);

  columnHeaders?.forEach((header) => {
    columnList.push({
      accessorFn: (originalRow: BundleUpchargeStoreGroup) => patchesIncludesPrice(originalRow?.patches, header),
      header: header.description,
      enableSorting: true,
      cell: renderStoreGroup
        ? PriceInfoGroupStore(header.description, handleInfoClick)
        : PriceInfoStores(header.description, handleInfoClick),
    });
  });

  columnList.push({
    id: '_',
    cell: (props: any) => (
      <button
        data-testid="remove-store-group"
        type="button"
        className={styles.removeButton}
        onClick={() => {
          if (renderStoreGroup) removeStoreGroup(props.row.original.storeGroupId);
          else removeStore(props.row.original.storeNo);
        }}
      >
        <X data-testid="x-icon" size={24} color="var(--black-900)" />
      </button>
    ),
  });

  return (
    <div className={styles.lineSeparator}>
      <div className={styles.currentSettings}>
        {t('bundleUpcharges.editDrawer.currentSettings.title')}
        <div className={styles.table}>
          {renderStoreGroup ? (
            <Table data={tableDataStoreGroup} columns={columnList} />
          ) : (
            <Table data={tableDataStore} columns={columnList} />
          )}
        </div>
      </div>

      {bundleUpchargePriceDeviationData.isBundleUpchargePriceDeviationOpen && (
        <BundleUpchargesPriceDeviationDrawer
          isOpen={bundleUpchargePriceDeviationData.isBundleUpchargePriceDeviationOpen}
          onClose={() =>
            setBundleUpchargePriceDeviationData({
              ...bundleUpchargePriceDeviationData,
              isBundleUpchargePriceDeviationOpen: false,
            })
          }
          type={bundleUpchargePriceDeviationData.type}
          groupId={bundleUpchargePriceDeviationData.groupId}
          cameFromEditDrawer
        />
      )}
    </div>
  );
};

export default CurrentSettings;
